import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled from 'styled-components';
import {
  brandColours,
  fontSize,
  maxBreakpointQuery,
  minBreakpointQuery,
  sectionPaddings,
  standardColours,
  standardTransition,
} from '../styles';
import { Container, Link, Svg } from './ui';
import NewsletterSignupForm from './NewsletterSignupForm';
import Logo from './Logo';
import facebookIcon from '../images/facebook-icon.inline.svg';
import twitterIcon from '../images/twitter-icon.inline.svg';
import instagramIcon from '../images/instagram-icon.inline.svg';
import linkedinIcon from '../images/linkedin-icon.inline.svg';
import youtubeIcon from '../images/youtube-icon.inline.svg';
import visaIcon from '../images/visa-icon.svg';
import mastercardIcon from '../images/mastercard-icon.svg';
import maestroIcon from '../images/maestro-icon.svg';
import paypalIcon from '../images/paypal-icon.svg';
import amexIcon from '../images/amex-icon.svg';

const StyledFooter = styled.footer`
  background-color: ${brandColours.primary};
  color: ${standardColours.white};
  border-top: 1px solid ${standardColours.white};
`;

const StyledContactInfo = styled.div`
  ${minBreakpointQuery.large`
    border-bottom: 1px solid ${standardColours.white};
  `}
`;

const StyledContainer = styled(Container)`
  padding-left: 0;
  padding-right: 0;
`;

const StyledContactInner = styled.div`
  ${minBreakpointQuery.small`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
  `}

  ${minBreakpointQuery.large`
    grid-template-columns: 1fr 475px 1fr;
  `}
`;

const StyledContact = styled.div`
  padding: 45px 30px;
  text-align: center;

  ${maxBreakpointQuery.large`
    border-bottom: 1px solid ${standardColours.white};
  `}

  ${minBreakpointQuery.small`
    border-right: 1px solid ${standardColours.white};
  `}

  ${minBreakpointQuery.smedium`
    padding-top: 55px;
    padding-bottom: 55px;
  `}

  ${minBreakpointQuery.medium`
    padding-top: 65px;
    padding-bottom: 65px;
  `}

  ${minBreakpointQuery.large`
    text-align: left;
    padding-top: 75px;
    padding-bottom: 75px;
  `}
`;

const StyledPhone = styled.p`
  ${fontSize(25)}
  margin-top: 15px;

  ${minBreakpointQuery.small`
    margin-top: 25px;
  `}
`;

const StyledSocial = styled.div`
  padding: 45px 30px;
  text-align: center;

  ${maxBreakpointQuery.large`
    border-bottom: 1px solid ${standardColours.white};
  `}

  ${minBreakpointQuery.smedium`
    padding-top: 55px;
    padding-bottom: 55px;
  `}

  ${minBreakpointQuery.medium`
    padding-top: 65px;
    padding-bottom: 65px;
  `}

  ${minBreakpointQuery.large`
    padding-top: 75px;
    padding-bottom: 75px;
  `}
`;

const StyledSocialLinks = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
  margin: 20px auto 0;
  max-width: 280px;
`;

const StyledSocialLink = styled.a`
  display: flex;
  padding: 14px;
  border-radius: 50%;
  border: 1px solid ${standardColours.transparentWhite(0.2)};
  transition: ${standardTransition('background-color')};

  &:hover {
    background-color: ${standardColours.transparentWhite(0.2)};
  }
`;

const StyledIcon = styled(Svg)`
  width: 16px;
  height: 16px;
  vertical-align: middle;

  path {
    fill: ${standardColours.white};
  }
`;

const StyledNewsletterSignup = styled.div`
  padding: 45px 30px;

  ${maxBreakpointQuery.large`
    border-bottom: 1px solid ${standardColours.white};
  `}

  ${minBreakpointQuery.small`
    grid-column: 1 / 3;
  `}

  ${minBreakpointQuery.smedium`
    padding-top: 55px;
    padding-bottom: 55px;
  `}

  ${minBreakpointQuery.medium`
    padding-top: 65px;
    padding-bottom: 65px;
  `}

  ${minBreakpointQuery.large`
    grid-column: auto;
    border-left: 1px solid ${standardColours.white};
    padding-top: 75px;
    padding-bottom: 75px;
    padding-right: 30px;
  `}
`;

const StyledNewsletterSignupInner = styled.div`
  ${minBreakpointQuery.large`
    max-width: 270px;
    margin: 0 auto;
  `}
`;

const StyledNewsletterSignupForm = styled(NewsletterSignupForm)`
  margin-top: 14px;
`;

const StyledMain = styled.div`
  ${sectionPaddings('50px', '90px')};
`;

const StyledWrapper = styled.div`
  display: grid;
  row-gap: 35px;

  ${minBreakpointQuery.small`
    grid-template-columns: repeat(2, 1fr);
  `}

  ${minBreakpointQuery.large`
    grid-template-columns: 1fr 475px 1fr;
  `}
`;

const StyledGroup = styled.div`
  padding: 0 30px;
`;

const StyledHeading = styled.h3`
  ${fontSize(18)}
`;

const StyledList = styled.ul`
  ${minBreakpointQuery.mlarge`
    margin-top: 25px;
  `}
`;

const StyledListItem = styled.li`
  margin: 15px 0;
`;

const StyledListLink = styled(Link)``;

const StyledAddress = styled.p`
  white-space: break-spaces;
  line-height: 2;
  margin-top: 20px;
`;

const StyledPaymentIcons = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 50px;

  ${maxBreakpointQuery.small`
    justify-content: center;
  `}
`;

const StyledPaymentIcon = styled.img`
  width: 50px;
  height: 31px;
  background-color: ${standardColours.white};
  border-radius: 5px;
`;

const StyledBottom = styled.div`
  padding-bottom: 50px;

  ${minBreakpointQuery.large`
    padding-bottom: 35px;
  `}
`;

const StyledBottomInner = styled.div`
  text-align: center;
  ${fontSize(14)}

  ${minBreakpointQuery.large`
    display: flex;
    align-items: center;
    justify-content: space-between;
  `}
`;

const StyledLogoCopyright = styled.div`
  ${minBreakpointQuery.large`
    display: flex;
    align-items: center;
  `}
`;

const StyledLogo = styled(Logo)`
  width: 175px;

  ${maxBreakpointQuery.large`
    margin-bottom: 12px;
  `}
`;

const StyledCopyright = styled.p`
  ${minBreakpointQuery.large`
    display: inline-block;
    margin-left: 15px;
  `}
`;

const StyledAdditionalLinks = styled.ul`
  ${maxBreakpointQuery.mlarge`
    margin-top: 32px;
  `}

  ${minBreakpointQuery.mlarge`
    display: flex;
    column-gap: 30px;
  `}

  ${minBreakpointQuery.large`
    column-gap: 40px;
  `}

  ${minBreakpointQuery.xlarge`
    column-gap: 50px;
  `}
`;

const StyledAdditionalLinkItem = styled.li`
  margin: 10px 0;
`;

const StyledAdditionalLink = styled(Link)`
  text-decoration: underline;
`;

const Footer = () => {
  const {
    datoCmsContactInformation: {
      phoneNumber,
      facebookProfile,
      twitterProfile,
      instagramProfile,
      linkedinProfile,
      youtubeProfile,
      address,
    },
    datoCmsFooter: { productLinks, supportLinks, additionalLinks },
  } = useStaticQuery(graphql`
    query FooterQuery {
      datoCmsContactInformation {
        phoneNumber
        facebookProfile
        twitterProfile
        instagramProfile
        linkedinProfile
        youtubeProfile
        address
      }
      datoCmsFooter {
        productLinks {
          id
          text
          pageUrl {
            ...LinkFragment
          }
        }
        supportLinks {
          id
          text
          pageUrl {
            ...LinkFragment
          }
        }
        additionalLinks {
          id
          text
          pageUrl {
            ...LinkFragment
          }
        }
      }
    }
  `);

  const socialProfiles = [
    {
      name: 'Facebook Profile',
      url: facebookProfile,
      icon: facebookIcon,
    },
    {
      name: 'Twitter Profile',
      url: twitterProfile,
      icon: twitterIcon,
    },
    {
      name: 'Instagram Profile',
      url: instagramProfile,
      icon: instagramIcon,
    },
    {
      name: 'LinkedIn Profile',
      url: linkedinProfile,
      icon: linkedinIcon,
    },
    {
      name: 'YouTube Profile',
      url: youtubeProfile,
      icon: youtubeIcon,
    },
  ];

  const paymentIcons = [
    {
      name: 'Visa',
      icon: visaIcon,
    },
    {
      name: 'Mastercard',
      icon: mastercardIcon,
    },
    {
      name: 'Maestro',
      icon: maestroIcon,
    },
    {
      name: 'American Express',
      icon: amexIcon,
    },
    {
      name: 'PayPal',
      icon: paypalIcon,
    },
  ];

  return (
    <StyledFooter>
      <StyledContactInfo>
        <StyledContainer>
          <StyledContactInner>
            <StyledContact>
              <p>
                <strong>Need help or advice?</strong>
              </p>
              <StyledPhone>{phoneNumber}</StyledPhone>
            </StyledContact>
            <StyledSocial>
              <p>
                <strong>Follow us</strong>
              </p>
              <StyledSocialLinks>
                {socialProfiles.map(({ name, url, icon }, id) => (
                  <StyledSocialLink
                    key={id}
                    href={url}
                    target="_blank"
                    rel="noopener"
                    aria-label={name}
                  >
                    <StyledIcon image={icon} />
                  </StyledSocialLink>
                ))}
              </StyledSocialLinks>
            </StyledSocial>
            <StyledNewsletterSignup>
              <StyledNewsletterSignupInner>
                <p>
                  <strong>Sign up to our mailing list</strong>
                </p>
                <StyledNewsletterSignupForm />
              </StyledNewsletterSignupInner>
            </StyledNewsletterSignup>
          </StyledContactInner>
        </StyledContainer>
      </StyledContactInfo>
      <StyledMain>
        <StyledContainer>
          <StyledWrapper>
            {productLinks.length > 0 && (
              <StyledGroup>
                <StyledHeading>Products</StyledHeading>
                <StyledList>
                  {productLinks.map(({ text, pageUrl }, id) => (
                    <StyledListItem key={id}>
                      <StyledListLink to={pageUrl}>{text}</StyledListLink>
                    </StyledListItem>
                  ))}
                </StyledList>
              </StyledGroup>
            )}
            {supportLinks.length > 0 && (
              <StyledGroup>
                <StyledHeading>Support</StyledHeading>
                <StyledList>
                  {supportLinks.map(({ text, pageUrl }, id) => (
                    <StyledListItem key={id}>
                      <StyledListLink to={pageUrl}>{text}</StyledListLink>
                    </StyledListItem>
                  ))}
                </StyledList>
              </StyledGroup>
            )}
            <StyledGroup>
              <StyledHeading>Head Office & Production</StyledHeading>
              <StyledAddress>{address}</StyledAddress>
              <StyledPaymentIcons>
                {paymentIcons.map(({ icon, name }, id) => (
                  <StyledPaymentIcon
                    key={id}
                    src={icon}
                    alt={name}
                    loading="lazy"
                  />
                ))}
              </StyledPaymentIcons>
            </StyledGroup>
          </StyledWrapper>
        </StyledContainer>
      </StyledMain>
      <StyledBottom>
        <Container>
          <StyledBottomInner>
            <StyledLogoCopyright>
              <StyledLogo alt={true} />
              <StyledCopyright>
                &copy; {new Date().getFullYear()}. All Rights Reserved
              </StyledCopyright>
            </StyledLogoCopyright>
            {additionalLinks.length > 0 && (
              <StyledAdditionalLinks>
                {additionalLinks.map(({ text, pageUrl }, id) => (
                  <StyledAdditionalLinkItem key={id}>
                    <StyledAdditionalLink to={pageUrl}>
                      {text}
                    </StyledAdditionalLink>
                  </StyledAdditionalLinkItem>
                ))}
              </StyledAdditionalLinks>
            )}
          </StyledBottomInner>
        </Container>
      </StyledBottom>
    </StyledFooter>
  );
};

export default Footer;
